import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getIntersectionRoot, getScrollEventTarget, getScrollTop, scrollTo, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLSpanElement} [viewMorePrevious]
 * @property {HTMLSpanElement} [viewMoreNext]
 * @property {HTMLElement[]} [cards]
 * @typedef {'previous'|'next'} Direction
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** @type {Map<number, string>} */
  pages = new Map();
  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;
  /** @type {IntersectionObserver | undefined} */
  #prefetchObserver;
  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;
  /** @type {Map<number, {promise: Promise<string | undefined>, controller: AbortController}>} */
  #requests = new Map();
  /** @type {Map<Direction, Promise<void>>} */
  #renders = new Map();
  /** @type {Map<number, {attempts: number, retryAt: number}>} */
  #failures = new Map();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #retryTimer;
  /** @type {EventTarget | undefined} */
  #scrollTarget;
  #boundaryFrame = 0;
  #generation = 0;
  #context = '';
  #waitingForFilter = false;
  #lookaheadArmed = false;
  #lastScrollTop = 0;
  #lastBoundaryScrollTop = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#reset();
    this.#waitingForFilter = false;
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard instanceof HTMLElement) this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    this.#lastScrollTop = getScrollTop();
    this.#lookaheadArmed = !this.matches('.pd-collection') || this.#lastScrollTop > 0;
    this.#connectScrollTarget();
    this.#observeViewMore();
    this.#scheduleBoundaryCheck();
    if (!this.matches('.pd-collection')) {
      this.#fetchPage('next');
      this.#fetchPage('previous');
    }
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    // Section/editor replacements can retain the custom element but replace its grid.
    // Filter events keep paging paused until their actual render promise settles.
    if (!this.#waitingForFilter) this.#reset();
    this.#observeViewMore();
    this.#scheduleBoundaryCheck();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reset();
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleScrollContainerChange);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #pageContext() {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return url.toString();
  }

  #reset() {
    ++this.#generation;
    this.#context = this.#pageContext();
    cancelAnimationFrame(this.#boundaryFrame);
    this.#boundaryFrame = 0;
    for (const request of this.#requests.values()) request.controller.abort();
    this.#requests.clear();
    this.#renders.clear();
    this.#failures.clear();
    this.pages.clear();
    clearTimeout(this.#retryTimer);
    this.#retryTimer = undefined;
    this.#disconnectObservers();
  }

  #disconnectObservers() {
    this.infinityScrollObserver?.disconnect();
    this.#prefetchObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    this.#prefetchObserver = undefined;
  }

  #connectScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = this.matches('.pd-collection') ? getScrollEventTarget() : document;
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #handleScroll = () => {
    const top = getScrollTop();
    if (top === this.#lastScrollTop) return;
    this.#lastScrollTop = top;
    if (!this.#lookaheadArmed || Math.abs(top - this.#lastBoundaryScrollTop) >= 800) this.#scheduleBoundaryCheck();
    // Do not spend on the next collection page until browsing starts. Restored
    // positions also arm this, while resize-only scroll events do not.
    if (!this.#lookaheadArmed && top > 0) {
      this.#lookaheadArmed = true;
      this.#observeViewMore();
    } else if ([...this.#failures.values()].some((failure) => Date.now() >= failure.retryAt)) {
      this.#observeViewMore();
      this.#scheduleBoundaryCheck();
    }
  };

  #scheduleBoundaryCheck() {
    if (this.#boundaryFrame || !this.matches('.pd-collection')) return;
    this.#boundaryFrame = requestAnimationFrame(() => {
      this.#boundaryFrame = 0;
      this.#lastBoundaryScrollTop = getScrollTop();
      if (!this.#lookaheadArmed || !this.#isCurrent(this.#generation)) return;
      const marker = this.refs.viewMoreNext;
      if (!marker) return;
      const rootTop = getIntersectionRoot()?.getBoundingClientRect().top ?? 0;
      // End/PageDown can jump completely over the sentinel and its observer
      // margin. Load one page, then recheck after insertion rather than stalling.
      if (marker.getBoundingClientRect().bottom < rootTop - 100) this.#renderPage('next');
    });
  }

  #handleScrollContainerChange = () => {
    this.#lastScrollTop = getScrollTop();
    this.#connectScrollTarget();
    this.#observeViewMore();
  };

  #observeViewMore() {
    this.#disconnectObservers();
    if (!this.isConnected || this.#waitingForFilter) return;
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;
    const collection = this.matches('.pd-collection');
    const root = collection ? getIntersectionRoot() : null;
    const generation = this.#generation;
    this.infinityScrollObserver = new IntersectionObserver(async (entries) => {
      if (viewTransition.current) await viewTransition.current;
      if (!this.#isCurrent(generation)) return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const type = this.#directionFor(entry.target);
        if (!type) continue;
        // Short lists may need to fill the actual viewport immediately, but a
        // generous insertion margin must not turn into eager initial fetching.
        const bounds = root?.getBoundingClientRect();
        const actuallyVisible = entry.boundingClientRect.top <= (bounds?.bottom ?? window.innerHeight) &&
          entry.boundingClientRect.bottom >= (bounds?.top ?? 0);
        if (type === 'previous' || this.#lookaheadArmed || actuallyVisible) this.#renderPage(type);
      }
    }, { root, rootMargin: collection ? '100px 0px 1600px 0px' : '100px' });
    if (collection) {
      this.#prefetchObserver = new IntersectionObserver((entries) => {
        if (!this.#isCurrent(generation)) return;
        for (const entry of entries) {
          const type = this.#directionFor(entry.target);
          if (entry.isIntersecting && type && (type === 'previous' || this.#lookaheadArmed)) this.#fetchPage(type);
        }
      }, { root, rootMargin: '1000px 0px 3200px 0px' });
    }
    for (const marker of [viewMorePrevious, viewMoreNext]) {
      if (!marker) continue;
      this.infinityScrollObserver.observe(marker);
      this.#prefetchObserver?.observe(marker);
    }
  }

  /** @param {Element} target @returns {Direction | undefined} */
  #directionFor(target) {
    if (target === this.refs.viewMorePrevious) return 'previous';
    if (target === this.refs.viewMoreNext) return 'next';
  }

  /** @param {number} generation @param {Element} [grid] */
  #isCurrent(generation, grid) {
    return this.isConnected && !this.#waitingForFilter && generation === this.#generation &&
      this.#context === this.#pageContext() && (!grid || grid === this.refs.grid);
  }

  /** @param {{page: number} | undefined} page */
  #shouldUsePage(page) {
    return !!page && Number.isInteger(page.page) && page.page >= 1 && page.page <= Number(this.refs.grid?.dataset.lastPage);
  }

  /** @param {Direction} type */
  #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return Promise.resolve(undefined);
    return this.#fetchSpecificPage(page.page, page.url);
  }

  /** @param {number} page @param {URL} url */
  #fetchSpecificPage(page, url) {
    const generation = this.#generation;
    if (!this.#isCurrent(generation)) return Promise.resolve(undefined);
    if (this.pages.has(page)) return Promise.resolve(this.pages.get(page));
    const pending = this.#requests.get(page);
    if (pending) return pending.promise;
    const failure = this.#failures.get(page);
    if (failure && Date.now() < failure.retryAt) return Promise.resolve(undefined);
    const controller = new AbortController();
    const promise = sectionRenderer.getSectionHTML(this.sectionId, !failure, url, controller.signal)
      .then((html) => {
        if (!this.#isCurrent(generation)) return;
        const cards = this.#getGridForPage(page, html);
        // Do not cache an error response or a mismatched page. A retry bypasses
        // the renderer cache, which may otherwise retain an HTTP error body.
        if (!cards?.length) throw new Error(`Page ${page} did not contain its expected cards`);
        this.pages.set(page, html);
        this.#failures.delete(page);
        return html;
      })
      .catch((error) => {
        if (controller.signal.aborted || !this.#isCurrent(generation)) return;
        const attempts = (failure?.attempts ?? 0) + 1;
        this.#failures.set(page, { attempts, retryAt: Date.now() + Math.min(4000, 1000 * 2 ** (attempts - 1)) });
        console.warn('Additional results could not be loaded:', error);
        // Bounded recovery while the shopper is waiting near the boundary.
        // Further scrolling can retry again without a permanently held lock.
        if (attempts <= 2) {
          clearTimeout(this.#retryTimer);
          this.#retryTimer = setTimeout(() => {
            this.#observeViewMore();
            this.#scheduleBoundaryCheck();
          }, Math.min(4000, 1000 * 2 ** (attempts - 1)) + 50);
        }
      })
      .finally(() => {
        if (this.#requests.get(page)?.promise === promise) this.#requests.delete(page);
      });
    this.#requests.set(page, { promise, controller });
    return promise;
  }

  /** @param {Direction} type */
  #renderPage(type) {
    if (this.#renders.has(type)) return;
    const generation = this.#generation;
    const promise = this.#loadAndRenderPage(type, generation).finally(() => {
      if (this.#renders.get(type) !== promise) return;
      this.#renders.delete(type);
    });
    this.#renders.set(type, promise);
  }

  /** @param {Direction} type @param {number} generation */
  async #loadAndRenderPage(type, generation) {
    const { grid } = this.refs;
    const page = this.#getPage(type);
    if (!grid || !page || !this.#shouldUsePage(page)) return;
    const html = await this.#fetchSpecificPage(page.page, page.url);
    if (!html || !this.#isCurrent(generation, grid) || this.#getPage(type)?.page !== page.page) return;
    const cards = this.#getGridForPage(page.page, html);
    if (!cards?.length || grid.querySelector(`:scope > [data-page="${page.page}"]`)) return;
    const firstElement = type === 'previous' ? grid.querySelector(':scope > [ref="cards[]"]') : null;
    const oldTop = firstElement?.getBoundingClientRect().top ?? 0;
    if (type === 'previous') grid.prepend(...cards);
    else grid.append(...cards);
    // Refresh immediately; relying only on the base observer can leave the next
    // sentinel callback reading the old last card during rapid scrolling.
    super.updatedCallback();
    this.#aspectRatioHelper?.processNewElements();
    if (firstElement) scrollTo({ top: getScrollTop() + firstElement.getBoundingClientRect().top - oldTop, behavior: 'instant' });
    await yieldToMainThread();
    if (!this.#isCurrent(generation, grid)) return;
    // Re-observing handles a fast jump where the sentinel remains intersecting
    // after insertion. Loading stops when the 1600px buffer is filled.
    this.#observeViewMore();
    this.#scheduleBoundaryCheck();
    if (!this.matches('.pd-collection')) requestIdleCallback(() => {
      if (this.#isCurrent(generation, grid)) this.#fetchPage(type);
    });
    // Automatic pagination deliberately leaves the URL and SEO metadata intact.
  }

  /** @param {Direction} type @returns {{page: number, url: URL} | undefined} */
  #getPage(type) {
    // Read current direct children, including before the base mutation observer runs.
    const cards = this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards?.length) return;
    const card = cards[type === 'previous' ? 0 : cards.length - 1];
    const page = Number(card?.getAttribute('data-page')) + (type === 'previous' ? -1 : 1);
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  /** @param {number} page @param {string} [html] @returns {NodeListOf<Element> | undefined} */
  #getGridForPage(page, html = this.pages.get(page)) {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const grid = parsed.querySelector('[ref="grid"]');
    const cards = grid?.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards?.length || [...cards].some((card) => Number(card.getAttribute('data-page')) !== page)) return;
    return cards;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  /** @param {Event & {promise?: Promise<unknown>}} event */
  #handleFilterUpdate = (event) => {
    const eventSection = event.target instanceof Element ? event.target.closest('[id^="shopify-section-"]') : null;
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#reset();
    this.#waitingForFilter = true;
    const generation = this.#generation;
    const settle = () => {
      if (!this.isConnected || generation !== this.#generation) return;
      this.#waitingForFilter = false;
      super.updatedCallback();
      this.#observeViewMore();
      this.#scheduleBoundaryCheck();
    };
    if (event.promise) event.promise.then(settle).catch((error) => {
      // Keep stale results from paging under a failed new filter URL. A later
      // successful filter/editor update or reconnect can restart pagination.
      if (generation === this.#generation) {
        this.#waitingForFilter = false;
        this.#context = '';
        console.warn('Filtered results could not be updated:', error);
      }
    });
    else {
      // Compatibility for an event producer without the standard render promise.
      requestAnimationFrame(settle);
    }
  };
}
